import { DeviceContext } from '@/hooks/common/DeviceContext';
import React, { FC, ReactElement, use } from 'react';
import GradientTextField from '@/components/ui/gradienttextfield';
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";

interface PromptInputProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  // Audio icon props
  showAudioIcon?: boolean;
  isAudioEnabled?: boolean;
  isAudioActive?: boolean;
  onAudioClick?: () => void;
  handleImageClick: () => void;
  imageUploadTitle?: string;
  handleTuneClick: () => void;
  renderGenerateButton: ReactElement
}

const ImageIcon: FC = () => (
  <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21.0938 4.6875H3.90625C3.47478 4.6875 3.125 5.03728 3.125 5.46875V19.5312C3.125 19.9627 3.47478 20.3125 3.90625 20.3125H21.0938C21.5252 20.3125 21.875 19.9627 21.875 19.5312V5.46875C21.875 5.03728 21.5252 4.6875 21.0938 4.6875Z" stroke="#000000" />
    <path d="M15.2344 9.09375C15.6054 9.09375 15.9062 9.39456 15.9062 9.76562C15.9062 10.1367 15.6054 10.4375 15.2344 10.4375C14.8633 10.4375 14.5625 10.1367 14.5625 9.76562C14.5625 9.39456 14.8633 9.09375 15.2344 9.09375Z" fill="#000000" stroke="#000000"/>
    <path d="M14.3867 16.0158L16.8955 13.5099C17.042 13.3635 17.2406 13.2812 17.4478 13.2812C17.6549 13.2812 17.8535 13.3635 18 13.5099L21.876 17.3888" stroke="#000000" />
    <path d="M3.125 16.4742L8.43164 11.1666C8.5042 11.094 8.59036 11.0364 8.6852 10.9971C8.78005 10.9577 8.88171 10.9375 8.98438 10.9375C9.08704 10.9375 9.1887 10.9577 9.28355 10.9971C9.37839 11.0364 9.46455 11.094 9.53711 11.1666L18.6826 20.3131" stroke="#000000" />
  </svg>
);

const TuneIcon = () => (
  <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10.1562 10.1562C11.4507 10.1562 12.5 9.10692 12.5 7.8125C12.5 6.51808 11.4507 5.46875 10.1562 5.46875C8.86183 5.46875 7.8125 6.51808 7.8125 7.8125C7.8125 9.10692 8.86183 10.1562 10.1562 10.1562Z" stroke="#707070" />
    <path d="M16.4062 19.5312C17.7007 19.5312 18.75 18.4819 18.75 17.1875C18.75 15.8931 17.7007 14.8438 16.4062 14.8438C15.1118 14.8438 14.0625 15.8931 14.0625 17.1875C14.0625 18.4819 15.1118 19.5312 16.4062 19.5312Z" stroke="#707070" />
    <path d="M12.5 7.8125H21.0938" stroke="#707070" />
    <path d="M3.90625 7.8125H7.8125" stroke="#707070" />
    <path d="M18.75 17.1875H21.0938" stroke="#707070" />
    <path d="M3.90625 17.1875H14.0625" stroke="#707070" />
  </svg>
)

const AudioIcon = React.memo(({ isEnabled, isActive }: { isEnabled: boolean; isActive: boolean }) => {
  if (isEnabled === false) {
    return <VolumeUpIcon className="text-gray-300 w-6 h-6 sm:w-8 sm:h-8 transition-colors" />;
  }
  if (isActive) {
    return <VolumeUpIcon className="text-gray-600 w-6 h-6 sm:w-8 sm:h-8 transition-colors" />;
  }
  return <VolumeOffIcon className="text-gray-600 w-6 h-6 sm:w-8 sm:h-8 transition-colors" />;
});

AudioIcon.displayName = 'AudioIcon';

// Helper function to generate styles based on device type
const getMobileTextFieldStyles = () => ({
  height: "103px",
  width: "359px",
  borderRadius: "20px",
  fontSize: "16px",
  fontFamily: "Maven Pro",
  fontWeight: "400",
  fontStyle: "normal",
  paddingTop: "0px",
  paddingBottom: "67px",
  paddingLeft: "10px",
  paddingRight: "0px",
  textAlign: "left" as const,
  verticalAlign: "top" as const,
  lineHeight: "100%",
  letterSpacing: "0%",
  display: "flex",
  alignItems: "flex-start" as const,
});

// Helper for tablet styles
const getTabletTextFieldStyles = (showAudioIcon: boolean) => ({
  height: "90px",
  borderRadius: "30px",
  fontSize: "18px",
  fontFamily: undefined,
  fontStyle: undefined,
  letterSpacing: undefined,
  paddingTop: "16px",
  paddingBottom: "8px",
  paddingLeft: "60px",
  paddingRight: showAudioIcon ? "60px" : "24px",
  textAlign: "start" as const,
  verticalAlign: "middle" as const,
  lineHeight: "1.5",
  display: "block",
  alignItems: "center" as const,
});

// Helper for desktop styles
const getDesktopTextFieldStyles = (showAudioIcon: boolean) => ({
  height: "49px",
  borderRadius: "25px",
  fontSize: "18px",
  fontFamily: undefined,
  fontStyle: undefined,
  letterSpacing: undefined,
  paddingTop: "16px",
  paddingBottom: "8px",
  paddingLeft: "24px",
  paddingRight: showAudioIcon ? "60px" : "24px",
  textAlign: "start" as const,
  verticalAlign: "middle" as const,
  lineHeight: "1.5",
  display: "block",
  alignItems: "center" as const,
});

// Main function with reduced complexity
const getTextFieldStyles = (
  isMobile: boolean,
  isTablet: boolean,
  showAudioIcon: boolean
) => {
  let baseStyles;

  if (isMobile) {
    baseStyles = getMobileTextFieldStyles();
  } else if (isTablet) {
    baseStyles = getTabletTextFieldStyles(showAudioIcon);
  } else {
    baseStyles = getDesktopTextFieldStyles(showAudioIcon);
  }

  const {
    height,
    borderRadius,
    fontSize,
    fontFamily,
    fontStyle,
    letterSpacing,
    paddingTop,
    paddingBottom,
    paddingLeft,
    paddingRight,
    textAlign,
    verticalAlign,
    lineHeight,
    display,
    alignItems,
  } = baseStyles;

  return {
    width: "100%",
    maxWidth: "100%",
    height,
    "& .MuiOutlinedInput-root": {
      width: "100%",
      maxWidth: "100%",
      height,
      background: "linear-gradient(135deg, #F39A81, #959BEA)",
      padding: isMobile ? "1px" : "2px",
      borderRadius,
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input": {
      fontSize,
      fontFamily: isMobile ? fontFamily : undefined,
      fontWeight: "400",
      fontStyle: isMobile ? fontStyle : undefined,
      letterSpacing: isMobile ? letterSpacing : undefined,
      boxShadow: "none",
      backgroundColor: "#ffffff",
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight,
      textAlign,
      verticalAlign,
      borderRadius: `calc(${borderRadius} - ${isMobile ? "1px" : "2px"})`,
      boxSizing: "border-box",
      lineHeight,
      display,
      alignItems,
      "&::placeholder": {
        color: isMobile ? "#C6C6C6" : "#9CA3AF",
        opacity: 1,
      },
      "&::selection": {
        backgroundColor: "rgba(147, 155, 237, 0.25) !important",
        color: "#000000 !important",
      },
      "&::-moz-selection": {
        backgroundColor: "rgba(147, 155, 237, 0.25) !important",
        color: "#000000 !important",
      },
    },
    marginBottom: 0,
  };
};

const PromptInput: React.FC<PromptInputProps> = ({
  value,
  onChange,
  onKeyDown,
  placeholder = "Ready when you are...",
  disabled = false,
  showAudioIcon = false,
  isAudioEnabled = true,
  isAudioActive = true,
  onAudioClick,
  handleImageClick,
  imageUploadTitle,
  handleTuneClick,
  renderGenerateButton
}) => {
  const { isMobile, isTablet } = use(DeviceContext) ?? { isMobile: false, isTablet: false };
  
  return (
    <div className="relative w-full">
      <GradientTextField
        name={`prompt-input-${crypto.randomUUID()}`}
        autoComplete='off'
        autoCorrect='off'
        autoCapitalize='off'
        spellCheck={false}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        sx={getTextFieldStyles(isMobile, isTablet, showAudioIcon)}
        isMobile={isMobile}
        slotProps={{
          input: {
            autoComplete: 'new-password',
            'aria-autocomplete': 'none',
            'data-form-type': 'other',
            'data-lpignore': 'true',
            'data-1p-ignore': 'true',
          } as any
        }}
      />

      {/* Mobile Layout - Icons positioned at bottom inside the input */}
      {isMobile && (
        <>
          {/* Image Icon - Left bottom with gradient border */}
          <button
            type="button"
            onClick={handleImageClick}
            title={`Add image (optional) - ${imageUploadTitle}`}
            className="absolute left-2 bottom-3 z-10 flex items-center justify-center rounded-full transition-colors border-transparent bg-clip-padding"
            style={{
              width: '35px',
              height: '35px',
              padding: '5px',
              borderRadius: '100px',
              borderWidth: '1px',
              opacity: 1,
              background: "linear-gradient(white, white) padding-box, linear-gradient(135deg, #F39A81, #959BEA) border-box",
            }}
          >
            <ImageIcon />
          </button>

          {/* Tune Icon - Right bottom before generate button */}
          <button
            type="button"
            onClick={handleTuneClick}
            className="absolute bottom-3 z-10 flex items-center justify-center p-2 hover:bg-gray-100 hover:bg-opacity-50 rounded-full transition-colors"
            style={{
              right: '57px',
              background: 'transparent',
              border: 'none',
            }}
          >
            <TuneIcon />
          </button>

          {/* Generate Button - Far right bottom */}
          <div className="absolute right-4 bottom-4 z-10">
            {renderGenerateButton}
          </div>
        </>
      )}
      
      {/* Audio Icon positioned inside the input */}
      {!isMobile && showAudioIcon && (
        <button
          type="button"
          onClick={onAudioClick}
          disabled={isAudioEnabled === false}
          className={`absolute right-3 top-1/2 transform -translate-y-1/2 z-10 p-1 rounded-full transition-colors focus:outline-none focus:ring-0 active:outline-none outline-none ${
            isAudioEnabled === false ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-200'
          }`}
          style={{ 
            background: 'transparent',
            border: 'none',
            boxShadow: 'none',
            outline: 'none'
          }}
          title={isAudioEnabled ? 'Toggle audio generation' : 'Audio generation not available for this model'}
        >
          <AudioIcon isEnabled={isAudioEnabled} isActive={isAudioActive} />
        </button>
      )}
    </div>
  );
};

export default PromptInput;
